<script lang="ts" setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { VendorEvent } from '@/utils/vendors/types'
import { regionConfig } from '@/config/region'

/**
 * Reusable map for any array of VendorEvents that have location data.
 * Renders events as interactive markers with pixel-distance clustering.
 * Clicking a marker/cluster emits `select` with the matching event IDs.
 */

const props = defineProps<{
  events: VendorEvent[]
  /** IDs currently highlighted in the event list. null = no filter active. */
  selectedIds: string[] | null
}>()

const emit = defineEmits<{
  (e: 'select', ids: string[]): void
  (e: 'clear'): void
}>()

interface MarkerCluster {
  events: VendorEvent[]
  lat: number
  lon: number
}

const mapContainer = ref<HTMLElement | null>(null)
let map: L.Map | null = null
let markerLayer: L.LayerGroup | null = null

const eventsWithLocation = computed(() =>
  props.events.filter(
    (e): e is VendorEvent & { location: NonNullable<VendorEvent['location']> } =>
      e.location !== undefined
  )
)

// ── Marker / cluster icon factories ───────────────────────────────────────────

function singleIcon(color: string): L.DivIcon {
  return new L.DivIcon({
    html: `<i class="q-icon mdi mdi-map-marker" style="font-size:28px;color:${color};filter:drop-shadow(0 1px 3px rgba(0,0,0,0.45))"></i>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    className: ''
  })
}

function clusterIcon(count: number, color: string): L.DivIcon {
  return new L.DivIcon({
    html: `<span style="position:relative;display:inline-flex;align-items:flex-end">
      <i class="q-icon mdi mdi-map-marker-multiple" style="font-size:32px;color:${color};filter:drop-shadow(0 1px 3px rgba(0,0,0,0.45))"></i>
      <span style="position:absolute;top:-2px;right:-10px;background:#1a237e;color:#fff;border-radius:50%;width:18px;height:18px;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center">${count}</span>
    </span>`,
    iconSize: [42, 32],
    iconAnchor: [16, 32],
    className: ''
  })
}

// ── Pixel-distance clustering ──────────────────────────────────────────────────

function buildClusters(pixelThreshold = 50): MarkerCluster[] {
  if (!map) return []

  const points = eventsWithLocation.value.map((e) => ({
    event: e,
    px: map!.latLngToContainerPoint(new L.LatLng(e.location.lat, e.location.lon))
  }))

  const clusters: MarkerCluster[] = []
  const used = new Set<string>()

  for (const p of points) {
    if (used.has(p.event.id)) continue
    used.add(p.event.id)

    const cluster: MarkerCluster = {
      events: [p.event],
      lat: p.event.location.lat,
      lon: p.event.location.lon
    }

    for (const q of points) {
      if (used.has(q.event.id)) continue
      if (Math.hypot(p.px.x - q.px.x, p.px.y - q.px.y) < pixelThreshold) {
        cluster.events.push(q.event)
        used.add(q.event.id)
      }
    }

    if (cluster.events.length > 1) {
      cluster.lat = cluster.events.reduce((s, e) => s + e.location!.lat, 0) / cluster.events.length
      cluster.lon = cluster.events.reduce((s, e) => s + e.location!.lon, 0) / cluster.events.length
    }

    clusters.push(cluster)
  }

  return clusters
}

// ── Render ─────────────────────────────────────────────────────────────────────

function renderMarkers() {
  if (!map || !markerLayer) return
  markerLayer.clearLayers()

  const clusters = buildClusters()
  const hasFilter = props.selectedIds !== null
  const selectedSet = new Set(props.selectedIds ?? [])

  for (const cluster of clusters) {
    const active = !hasFilter || cluster.events.some((e) => selectedSet.has(e.id))
    const color = active ? '#1565C0' : '#90A4AE'
    const ids = cluster.events.map((e) => e.id)

    const icon =
      cluster.events.length > 1 ? clusterIcon(cluster.events.length, color) : singleIcon(color)

    const m = new L.Marker([cluster.lat, cluster.lon], { icon })
    m.on('click', () => emit('select', ids))
    markerLayer!.addLayer(m)
  }
}

function fitToMarkers() {
  if (!map || eventsWithLocation.value.length === 0) return
  const bounds = new L.LatLngBounds(
    eventsWithLocation.value.map((e) => [e.location.lat, e.location.lon] as [number, number])
  )
  map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 })
}

// ── Init ───────────────────────────────────────────────────────────────────────

function initMap() {
  if (!mapContainer.value) return

  const street = new L.TileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 19
  })
  const satellite = new L.TileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    {
      attribution:
        'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      maxZoom: 19
    }
  )

  map = new L.Map(mapContainer.value, {
    layers: [street],
    maxBounds: new L.LatLngBounds(
      [regionConfig.mapBounds.sw.lat, regionConfig.mapBounds.sw.lng],
      [regionConfig.mapBounds.ne.lat, regionConfig.mapBounds.ne.lng]
    ),
    maxBoundsViscosity: 0.8
  })

  new L.Control.Layers(
    { Aerial: satellite, Street: street },
    {},
    { position: 'topright', collapsed: true }
  ).addTo(map)

  markerLayer = new L.LayerGroup().addTo(map)

  // zoomend fires after fitBounds completes — use it as the primary render trigger
  map.on('zoomend', renderMarkers)
  // moveend covers panning without zoom change
  map.on('moveend', renderMarkers)

  fitToMarkers()
  // Render once immediately in case the map was already at the right zoom
  renderMarkers()
}

onMounted(initMap)

onUnmounted(() => {
  map?.remove()
  map = null
})

watch(
  () => props.events,
  () => {
    fitToMarkers()
    renderMarkers()
  }
)

// Re-colour markers when the selection changes without a zoom/move
watch(() => props.selectedIds, renderMarkers)
</script>

<template>
  <div class="vendor-event-map-wrapper">
    <div ref="mapContainer" class="vendor-event-map" />

    <!-- Clear-filter pill — sits over the bottom-centre of the map -->
    <transition name="fade">
      <div v-if="selectedIds !== null" class="map-clear-btn">
        <q-btn
          dense
          unelevated
          size="sm"
          color="blue-8"
          icon="mdi-filter-remove"
          label="Show all events"
          @click="emit('clear')"
        />
      </div>
    </transition>
  </div>
</template>

<style scoped>
.vendor-event-map-wrapper {
  position: relative;
  width: 100%;
}

.vendor-event-map {
  height: 350px;
  width: 100%;
  border-radius: 4px;
  z-index: 0;
}

.map-clear-btn {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
